import { readCircuitKey } from "../circuitKeys";
import type {
  KarlCircuit,
  KarlSchematicWay,
  KarlStepsResult,
} from "../karlSteps";
import type { ReadRouteWhy, RouteWhy, RouteWhyKind } from "./types";

/** The run writes this in front of a group it bucketed by symbol, not by a key. */
/** Same rule the schematic lookup uses: "-0F5", "0F5" and "0f5" are one tag. */
const normalize = (value: string): string =>
  value.toUpperCase().replace(/[^A-Z0-9]/g, "");

/**
 * `overview.notes` labels are already translated, so the grouping note can only
 * be found by its published heading — the i18n key itself covers an identity `t`.
 */
const GROUPING_NOTE_LABELS = new Set<string>([
  "smartSurveyor.cablesBeta.stepOverviewGrouping",
  "How circuits were formed",
  "Wie die Stromkreise gebildet wurden",
]);

interface RouteCircuitEntry {
  circuit: KarlCircuit;
  /** The feed point that holds it. Empty when no source claimed it. */
  board: string;
}

interface RouteIndex {
  circuits: Map<string, RouteCircuitEntry>;
  waysByKey: Map<string, KarlSchematicWay[]>;
  /** How many schedule rows each board carries — zero means no schedule at all. */
  waysByBoard: Map<string, number>;
  tagsOnPlan: Set<string>;
  note: string;
}

const readGroupingNote = (steps: KarlStepsResult): string => {
  const note = (steps.overview?.notes ?? []).find((entry) =>
    GROUPING_NOTE_LABELS.has(entry.label),
  );
  return note?.text ?? "";
};

const buildRouteIndex = (steps: KarlStepsResult): RouteIndex => {
  const circuits = new Map<string, RouteCircuitEntry>();
  for (const assignment of steps.assignments) {
    for (const circuit of assignment.circuits) {
      if (!circuits.has(circuit.circuitId)) {
        circuits.set(circuit.circuitId, {
          circuit,
          board: assignment.sourceName,
        });
      }
    }
  }

  // Indexed under EVERY identifier the row carries, not just the one the
  // detected column named. A schedule prints the plan's tag in `cable_id` on
  // some rows and `nr` on others — on project 153 that is 9 rows against 42 —
  // and indexing by one column alone loses the smaller set entirely, which is
  // why a run could read "printed on this plan" and still offer no schematic
  // page: its way was never found, so its breaker was never known.
  const waysByKey = new Map<string, KarlSchematicWay[]>();
  for (const way of steps.ways) {
    const identifiers = new Set(
      [way.key, ...(way.aliases ?? [])].map(normalize).filter(Boolean),
    );
    for (const identifier of identifiers) {
      const found = waysByKey.get(identifier);
      if (found) found.push(way);
      else waysByKey.set(identifier, [way]);
    }
  }

  const waysByBoard = new Map<string, number>();
  for (const way of steps.ways) {
    const board = normalize(way.board);
    if (!board) continue;
    waysByBoard.set(board, (waysByBoard.get(board) ?? 0) + 1);
  }

  return {
    circuits,
    waysByKey,
    waysByBoard,
    tagsOnPlan: new Set(steps.tagsOnPlan.map(normalize).filter(Boolean)),
    note: readGroupingNote(steps),
  };
};

// The contract passes one circuit at a time, so without this the tag set and the
// way map would be rebuilt 83 times for one walkthrough.
const indexCache = new WeakMap<KarlStepsResult, RouteIndex>();

const routeIndexOf = (steps: KarlStepsResult): RouteIndex => {
  const cached = indexCache.get(steps);
  if (cached) return cached;
  const built = buildRouteIndex(steps);
  indexCache.set(steps, built);
  return built;
};

const matchWay = (
  index: RouteIndex,
  key: string,
  circuitId: string,
): KarlSchematicWay | null => {
  const ways = index.waysByKey.get(normalize(key));
  if (!ways?.length) return null;
  // One key can appear on two boards; the way that already lists this circuit is
  // the one the run actually joined it to.
  return ways.find((way) => way.circuitIds.includes(circuitId)) ?? ways[0];
};

const decideKind = (
  key: string | null,
  onPlan: boolean,
  way: KarlSchematicWay | null,
): RouteWhyKind => {
  if (!key) return "grouped_by_symbol";
  if (onPlan) return "key_on_plan";
  if (way) return "key_from_schematic";
  return "board_only";
};

export const readRouteWhy: ReadRouteWhy = ({ steps, circuitId }) => {
  const index = routeIndexOf(steps);
  const found = index.circuits.get(circuitId);
  if (!found) {
    return {
      kind: "board_only",
      key: null,
      board: null,
      breaker: null,
      cableId: null,
      cableType: "",
      note: index.note,
      scheduleRowsForBoard: 0,
    };
  }

  const key = readCircuitKey({ circuit: found.circuit });
  const way = key ? matchWay(index, key, circuitId) : null;
  const onPlan = key ? index.tagsOnPlan.has(normalize(key)) : false;

  const why: RouteWhy = {
    kind: decideKind(key, onPlan, way),
    key,
    board: way ? way.board : found.board || null,
    breaker: way ? way.breaker : null,
    cableId: way?.cableId || null,
    cableType: found.circuit.cableType || way?.cableType || "",
    note: index.note,
    scheduleRowsForBoard:
      index.waysByBoard.get(normalize(way ? way.board : found.board || "")) ?? 0,
  };
  return why;
};
